"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { PyramidChart } from "@/components/tools/earth-science/pyramid-chart";
import {
  PyramidNotices,
  PyramidSummary,
} from "@/components/tools/earth-science/pyramid-summary";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  ageStats,
  type DisplayBasis,
  displayValues,
  type InputBasis,
  parsePyramid,
  totalOf,
} from "@/lib/tool-kit/earth-science/population-pyramid";

const EXAMPLE_A = `Age group, Male, Female
0–4, 2480, 2360
5–9, 2640, 2510
10–14, 2750, 2620
15–19, 2860, 2720
20–24, 3200, 3070
25–29, 3330, 3250
30–34, 3320, 3270
35–39, 3200, 3180
40–44, 3190, 3200
45–49, 3380, 3420
50–54, 3490, 3560
55–59, 3330, 3450
60–64, 2900, 3080
65–69, 2430, 2700
70–74, 1850, 2150
75–79, 1290, 1700
80–84, 820, 1280
85+, 520, 1150`;

const EXAMPLE_B = `Age group, Male, Female
0–4, 5200, 5000
5–9, 4700, 4550
10–14, 4300, 4200
15–19, 3800, 3700
20–24, 3300, 3250
25–29, 2800, 2800
30–34, 2400, 2400
35–39, 2000, 2020
40–44, 1650, 1700
45–49, 1350, 1400
50–54, 1100, 1150
55–59, 850, 900
60–64, 650, 720
65–69, 480, 560
70–74, 330, 420
75–79, 200, 290
80–84, 100, 170
85+, 50, 100`;

/** English strings. A translated content file overrides these through `ui`. */
export const POPULATION_PYRAMID_MAKER_STRINGS = {
  dataLabel:
    "Population data — one age group per line: age group, males, females",
  dataHint:
    "Five-year groups such as 0–4, 5–9 … 85+, youngest or oldest first. A first line of words names the columns. Separate cells with commas, tabs or semicolons.",
  exampleDataA: EXAMPLE_A,
  exampleDataB: EXAMPLE_B,
  nameLabel: "Name of the population",
  exampleNameA: "Country A",
  exampleNameB: "Country B",
  exampleTitle: "Population pyramid, Country A (illustrative data, thousands)",
  basisLabel: "Your values are",
  basisCounts: "Numbers of people",
  basisPercent: "Percentages of the population",
  displayLabel: "Draw the pyramid as",
  displayPercent: "Percent of the population",
  displayCounts: "Numbers of people",
  compare: "Compare with a second population (drawn as an outline)",
  compareDataLabel: "Comparison population — same age groups, same layout",
  compareNameLabel: "Name of the comparison population",
  compareMismatch:
    "The comparison population needs exactly the same age groups as the first. It is not drawn until they match.",
  compareNeedsPercent:
    "Populations of different sizes compare best as percentages, so the comparison is drawn as percent of each population.",
  male: "Male",
  female: "Female",
  outline: "Outline: {name}",
  labelsAndStyle: "Labels and style",
  chartTitle: "Chart title",
  xAxisTitle: "Horizontal axis title",
  axisTitlePlaceholderX: "Percent of the population",
  yAxisTitle: "Age axis title",
  axisTitlePlaceholderY: "Age group",
  defaultAxisPercent: "Percent of the population (%)",
  defaultAxisCounts: "Number of people",
  defaultAgeTitle: "Age group",
  colorLabel: "Color of the male side (the female side takes the next color)",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  valueLabels: "Show the values on the bars",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and the gridlines out of the plot, and sets the figure number in bold and the title in italics above the chart — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the data as a table",
  columnAge: "Age group",
  chartLabel: "Population pyramid",
  chartDescription:
    "Population pyramid of {name} with {count}, males on the left and females on the right. Largest male group {maleGroup}, {maleValue}; largest female group {femaleGroup}, {femaleValue}.",
  groupCount: "one:{count} age group|other:{count} age groups",
  summaryHeading: "Age structure",
  statMetric: "Figure",
  statUnder15: "Under 15",
  statWorking: "Aged 15–64",
  statOver65: "Aged 65 and over",
  statDependency: "Dependency ratio (per 100 aged 15–64)",
  statYouth: "Youth dependency ratio",
  statOldAge: "Old-age dependency ratio",
  statSexRatio: "Sex ratio (males per 100 females)",
  statMedian: "Median age (years)",
  statTotal: "Total population",
  notAvailable: "—",
  enterData:
    "Enter at least two age groups with numbers for males and females to draw the pyramid.",
  skippedLines:
    "one:Line {lines} has no numbers and was left out.|other:Lines {lines} have no numbers and were left out.",
  missingCells:
    "one:{count} cell is empty or not a number and was counted as zero.|other:{count} cells are empty or not numbers and were counted as zero.",
  negativeCells:
    "one:{count} negative value was drawn as its absolute value.|other:{count} negative values were drawn as their absolute values.",
  reordered: "The age groups were put in order from youngest to oldest.",
} as const;

function sameGroups(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((label, index) => label === b[index]);
}

export default function PopulationPyramidMakerWidget() {
  const strings = useToolUi(POPULATION_PYRAMID_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleDataA);
  const [rawB, setRawB] = useState<string>(strings.exampleDataB);
  const [nameA, setNameA] = useState<string>(strings.exampleNameA);
  const [nameB, setNameB] = useState<string>(strings.exampleNameB);
  const [basis, setBasis] = useState<InputBasis>("counts");
  const [display, setDisplay] = useState<DisplayBasis>("percent");
  const [compare, setCompare] = useState(false);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const data = parsePyramid(raw);
  const other = parsePyramid(rawB);
  const labels = data.groups.map((group) => group.label);
  const canCompare =
    compare &&
    sameGroups(
      labels,
      other.groups.map((g) => g.label)
    );
  // A comparison of two sizes is only fair as shares; counts stay available for one population.
  const shown: DisplayBasis =
    compare || basis === "percent" ? "percent" : display;
  const primary = displayValues(data.groups, basis, shown);
  const second = canCompare ? displayValues(other.groups, basis, shown) : null;
  const maleName = data.maleName ?? strings.male;
  const femaleName = data.femaleName ?? strings.female;
  const unit = shown === "percent" ? " %" : "";
  const xTitle =
    style.xTitle.trim() ||
    (shown === "percent"
      ? strings.defaultAxisPercent
      : strings.defaultAxisCounts);
  const yTitle = style.yTitle.trim() || strings.defaultAgeTitle;
  const stats = ageStats(data.groups);
  const columns = [
    { name: nameA, stats, total: totalOf(data.groups) },
    ...(canCompare
      ? [
          {
            name: nameB,
            stats: ageStats(other.groups),
            total: totalOf(other.groups),
          },
        ]
      : []),
  ];
  const peak = (values: number[]) => {
    const top = Math.max(...values);
    return {
      group: labels[values.indexOf(top)] ?? "",
      value: `${fmt(top, { maximumFractionDigits: 2 })}${unit}`,
    };
  };
  const description = (): string => {
    const male = peak(primary.male);
    const female = peak(primary.female);
    return t(strings.chartDescription, {
      name: nameA,
      count: tPlural(strings.groupCount, locale, labels.length, {
        count: fmt(labels.length),
      }),
      maleGroup: male.group,
      maleValue: male.value,
      femaleGroup: female.group,
      femaleValue: female.value,
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-[1fr_16rem]">
        <TextAreaField
          hint={strings.dataHint}
          id="ppm-data"
          label={strings.dataLabel}
          onChange={setRaw}
          rows={9}
          value={raw}
        />
        <TextField
          id="ppm-name"
          label={strings.nameLabel}
          onChange={setNameA}
          value={nameA}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.basisLabel}
          name="ppm-basis"
          onChange={setBasis}
          options={[
            { value: "counts", label: strings.basisCounts },
            { value: "percent", label: strings.basisPercent },
          ]}
          value={basis}
        />
        {basis === "counts" && !compare ? (
          <PillGroup
            label={strings.displayLabel}
            name="ppm-display"
            onChange={setDisplay}
            options={[
              { value: "percent", label: strings.displayPercent },
              { value: "counts", label: strings.displayCounts },
            ]}
            value={display}
          />
        ) : null}
      </div>
      <Checkbox
        checked={compare}
        label={strings.compare}
        onChange={setCompare}
      />
      {compare ? (
        <div className="grid gap-4 sm:grid-cols-[1fr_16rem]">
          <TextAreaField
            id="ppm-data-b"
            label={strings.compareDataLabel}
            onChange={setRawB}
            rows={9}
            value={rawB}
          />
          <TextField
            id="ppm-name-b"
            label={strings.compareNameLabel}
            onChange={setNameB}
            value={nameB}
          />
        </div>
      ) : null}
      <PyramidNotices data={data} fmt={fmt} labels={strings} locale={locale} />
      {compare && !canCompare ? (
        <Notice>{strings.compareMismatch}</Notice>
      ) : null}
      {compare && basis === "counts" ? (
        <p className={HINT}>{strings.compareNeedsPercent}</p>
      ) : null}
      {data.groups.length >= 2 ? (
        <>
          <ChartFigure
            apa={style.apa}
            data={toTsv([
              [strings.columnAge, maleName, femaleName],
              ...data.groups.map((group, index) => [
                group.label,
                primary.male[index],
                primary.female[index],
              ]),
            ])}
            figureNumber={style.figureNumber}
            filename={chartFilename(style.title, "population-pyramid")}
            labels={strings}
            note={style.note}
            table={{
              head: [strings.columnAge, maleName, femaleName],
              rows: data.groups.map((group, index) => [
                group.label,
                `${fmt(primary.male[index], { maximumFractionDigits: 2 })}${unit}`,
                `${fmt(primary.female[index], { maximumFractionDigits: 2 })}${unit}`,
              ]),
            }}
            title={style.title}
          >
            <PyramidChart
              comparison={second}
              comparisonName={t(strings.outline, { name: nameB })}
              description={description()}
              femaleName={femaleName}
              fmt={fmt}
              id="ppm-chart"
              label={style.title.trim() || strings.chartLabel}
              labels={labels}
              maleName={maleName}
              primary={primary}
              style={style}
              xTitle={xTitle}
              yTitle={yTitle}
            />
          </ChartFigure>
          <PyramidSummary
            columns={columns}
            fmt={fmt}
            labels={strings}
            showTotal={basis === "counts"}
          />
        </>
      ) : (
        <Notice>{strings.enterData}</Notice>
      )}
      <ChartStyleFields
        axisLabels={strings}
        colorLabels={strings}
        idPrefix="ppm"
        labels={strings}
        style={style}
        update={update}
        valueLabel={strings.valueLabels}
      />
    </div>
  );
}
